import { MouseEvent, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useCheckoutStoreContext } from '../../context';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { TOrder } from 'src/types/orders';
import { formatPrice } from 'src/utils/formatter';
import { formatDate } from 'src/utils/helpers';
import { confirmMessage } from 'src/utils/modalConfirmations';
import ImageSlider from 'src/view/components/ImageSlider/ImageSlider';
import './style.scss';

interface IProps {
  order?: TOrder;
}

const PreviousOrdersListOrder: React.FC<IProps> = ({ order }: IProps): JSX.Element => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const onOpenPreviousOrderModal = () => {
    if (!order) return;
    modalStore.openPreviousOrderModal({ id: Number(order.id) });
  };

  const onOpenConfirmReorderModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      confirmMessage({
        title: localeStore.t('are_you_sure'),
        message: localeStore.t('all_products_will_be_replaced'),
        onConfirm: () => checkoutStore.reorder(order),
      });
    },
    [order]
  );

  const orderProductsImages = useMemo((): string[] => {
    if (!order?.products?.length) return [];
    return order.products.map((product) => product.image);
  }, [order]);

  return (
    <div onClick={onOpenPreviousOrderModal} className="previous-order-card">
      <div className="previous-order-card__image-wrapper">
        {/* order products images */}
        <ImageSlider images={orderProductsImages} aspectRatio={2.16} withoutManagementElements />
        {/* order status */}
        <p className="previous-order-card__status">{order ? localeStore.t(`${order.order_status_code}_status`) : <Skeleton width={50} />}</p>
      </div>
      {/* order id */}
      <p className="family-semibold color-dark text-size-16 font-weight-600 mt-3"># {order ? order.order_key : <Skeleton width={100} />}</p>
      {/* order price */}
      <p className="family-medium color-dark text-size-12 font-weight-500 letter-spacing-12 my-2">
        {order ? formatPrice(order.total) : <Skeleton width={46} />}
      </p>
      {/* order date */}
      <p className="family-medium color-dark text-size-12 font-weight-500 letter-spacing-12">
        {order ? formatDate(order.created_at, {}) : <Skeleton width={139} />}
      </p>
      {/* reorder button */}
      <button onClick={onOpenConfirmReorderModal} className="previous-order-card__reorder-button letter-spacing-12">
        {localeStore.t('reorder')}
      </button>
    </div>
  );
};

export default PreviousOrdersListOrder;
